import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader";

export default class SceneInit {
  scene: THREE.Scene | undefined;
  camera: THREE.PerspectiveCamera | undefined;
  renderer: THREE.Renderer | undefined;
  fov: number;
  nearPlane: number;
  canvasId: string;
  clock: THREE.Clock | undefined;
  ambientLight: THREE.AmbientLight | undefined;
  directionalLight: THREE.DirectionalLight | undefined;
  directionalLight2: THREE.DirectionalLight | undefined;
  controls: OrbitControls | undefined;

  torus: THREE.Mesh | undefined;
  raycaster: THREE.Raycaster | undefined;
  pointer: THREE.Vector2 | undefined;
  container: any | undefined;
  selectedUuid: string | undefined;

  coarseGrid: THREE.Line | undefined;
  fineGrid: THREE.Line | undefined;
  zeroBoxes: THREE.Mesh[];
  outlineBoxes: THREE.Mesh[];

  horizontalHighlighter: THREE.Mesh | undefined;
  verticalHighlighter: THREE.Mesh | undefined;

  mouseDown: boolean;
  cameraMovement: boolean;
  mouseStartPos: THREE.Vector2;

  constructor(canvasId: string) {
    // NOTE: Core components to initialize Three.js app.
    this.scene = undefined;
    this.camera = undefined;
    this.renderer = undefined;

    // NOTE: Camera params;
    this.fov = 45;
    this.nearPlane = 1;
    this.nearPlane = 10000;
    this.canvasId = canvasId;

    // NOTE: Additional components.
    this.clock = undefined;
    this.controls = undefined;

    // NOTE: Lighting is basically required.
    this.ambientLight = undefined;
    this.directionalLight = undefined;
    this.directionalLight2 = undefined;

    this.torus = undefined;

    this.coarseGrid = undefined;
    this.fineGrid = undefined;
    this.zeroBoxes = [];
    this.outlineBoxes = [];

    this.raycaster = undefined;
    this.pointer = undefined;
    this.container = undefined;
    this.selectedUuid = undefined;

    this.horizontalHighlighter = undefined;
    this.verticalHighlighter = undefined;

    this.mouseDown = false;
    this.cameraMovement = false;

    this.mouseStartPos = new THREE.Vector2(0, 0);
  }

  initialize() {
    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.container = document.getElementById("3d-view-canvas")!;

    this.scene = new THREE.Scene();
    const canvas = document.getElementById(this.canvasId) as HTMLCanvasElement;

    this.camera = new THREE.PerspectiveCamera(
      this.fov,
      canvas.clientWidth / canvas.clientHeight,
      1,
      3000
    );
    // NOTE: Specify a canvas which is already created in the HTML.
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
    });
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    const div = document.getElementById("3d-view-wrapper");
    div!.appendChild(this.renderer!.domElement);

    this.clock = new THREE.Clock();
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);

    this.scene.background = new THREE.Color(0xa0a0a0);

    this.loadLighting();

    this.loadPlane();
    this.loadTorus();

    this.loadDirectionMarker();
    this.loadGrid();
    this.loadZeroOutlineBoxes();

    this.loadGridHighlighter();

    this.loadAxisDescription();

    // if window resizes
    window.addEventListener("resize", () => this.onWindowResize(), false);
    document.addEventListener("mouseup", (e) => this.onMouseUp(e), false);
    window.addEventListener("pointermove", (e) => this.onPointerMove(e));
    document.addEventListener("mousedown", (e) => this.onMouseDown(e), false);
  }

  loadLighting() {
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.9);
    this.ambientLight.castShadow = true;
    this.scene!.add(this.ambientLight);

    this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    this.directionalLight.position.set(0, 32, 64);
    this.scene!.add(this.directionalLight);

    this.directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.7);
    this.directionalLight2.position.set(0, 32, -64);
    this.scene!.add(this.directionalLight2);
  }

  loadPlane() {
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(1600, 1600),
      new THREE.MeshPhongMaterial({ color: "0xFFF8DC", depthWrite: false })
    );
    //Disable specular lighting for the plane

    plane.material.specular = new THREE.Color("0xFFF8DC");
    plane.material.shininess = 0.8;
    plane.rotation.x = -Math.PI / 2;
    plane.receiveShadow = true;
    plane.position.x = 0;
    plane.position.y = -100;
    plane.position.z = 0;
    this.scene!.add(plane);
  }

  loadTorus() {
    //Construct a torus (dark grey color) and add it to the scene
    const geometry = new THREE.TorusGeometry(5, 0.5, 12, 50, 5.5);
    const material = new THREE.MeshPhongMaterial({ color: 0x222222 });
    this.torus = new THREE.Mesh(geometry, material);
    this.torus.position.set(0, 0, 0);
    this.torus.scale.set(6, 6, 6);
    this.torus.rotation.set(Math.PI / 2, 0, 0);
    this.torus.visible = false;
    this.scene!.add(this.torus);
  }

  loadDirectionMarker() {
    const markerLineWidth = 4;
    const markerLineLength = 44;

    const markerMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
    const markerGeometry = new THREE.BoxGeometry(
      markerLineWidth,
      markerLineWidth,
      markerLineLength
    );

    const leftMarkerLine = new THREE.Mesh(markerGeometry, markerMaterial);
    const rightMarkerLine = new THREE.Mesh(markerGeometry, markerMaterial);

    leftMarkerLine.position.set(-15, -100, -800 - 0.4 * markerLineLength);
    rightMarkerLine.position.set(15, -100, -800 - 0.4 * markerLineLength);

    leftMarkerLine.rotation.set(0, -Math.PI / 4, 0);
    rightMarkerLine.rotation.set(0, Math.PI / 4, 0);

    this.scene!.add(leftMarkerLine);
    this.scene!.add(rightMarkerLine);
  }

  loadGrid() {
    const coarseGridMaterial = new THREE.LineBasicMaterial({ color: 0x333333 });
    const fineGridMaterial = new THREE.LineBasicMaterial({ color: 0x999999 });

    const coarseGridPoints = [];
    const fineGridPoints = [];

    //Resolution of 16 produces a line per metre
    const gridRes = 16;

    //Vertical lines
    for (let i = 0; i < gridRes + 1; i++) {
      if (i % 2 == 0) {
        coarseGridPoints.push(
          new THREE.Vector3(-800 + i * (1 / gridRes) * 1600, -100, -800)
        );
        coarseGridPoints.push(
          new THREE.Vector3(-800 + i * (1 / gridRes) * 1600, -100, 800)
        );
      } else {
        coarseGridPoints.push(
          new THREE.Vector3(-800 + i * (1 / gridRes) * 1600, -100, 800)
        );
        coarseGridPoints.push(
          new THREE.Vector3(-800 + i * (1 / gridRes) * 1600, -100, -800)
        );
      }
    }

    for (let i = 0; i < gridRes; i++) {
      if (i % 2 == 0) {
        fineGridPoints.push(
          new THREE.Vector3(
            -800 + (1 / gridRes) * 1600 * 0.5 + i * (1 / gridRes) * 1600,
            -100,
            -800
          )
        );
        fineGridPoints.push(
          new THREE.Vector3(
            -800 + (1 / gridRes) * 1600 * 0.5 + i * (1 / gridRes) * 1600,
            -100,
            800
          )
        );
      } else {
        fineGridPoints.push(
          new THREE.Vector3(
            -800 + (1 / gridRes) * 1600 * 0.5 + i * (1 / gridRes) * 1600,
            -100,
            800
          )
        );
        fineGridPoints.push(
          new THREE.Vector3(
            -800 + (1 / gridRes) * 1600 * 0.5 + i * (1 / gridRes) * 1600,
            -100,
            -800
          )
        );
      }
    }

    //We need a connection between the last point of the vertical lines and the first point
    //of the horizontal lines. Otherwise a diognal will appear on the dance floor.
    coarseGridPoints.push(new THREE.Vector3(800, -100, -800));
    fineGridPoints.push(new THREE.Vector3(750, -100, -750));

    //Horizontal lines
    for (let i = 0; i < gridRes + 1; i++) {
      if (i % 2 == 0) {
        coarseGridPoints.push(
          new THREE.Vector3(-800, -100, -800 + i * (1 / gridRes) * 1600)
        );
        coarseGridPoints.push(
          new THREE.Vector3(800, -100, -800 + i * (1 / gridRes) * 1600)
        );
      } else {
        coarseGridPoints.push(
          new THREE.Vector3(800, -100, -800 + i * (1 / gridRes) * 1600)
        );
        coarseGridPoints.push(
          new THREE.Vector3(-800, -100, -800 + i * (1 / gridRes) * 1600)
        );
      }
    }

    for (let i = 0; i < gridRes; i++) {
      if (i % 2 == 0) {
        fineGridPoints.push(
          new THREE.Vector3(
            -800,
            -100,
            -800 + (1 / gridRes) * 1600 * 0.5 + i * (1 / gridRes) * 1600
          )
        );
        fineGridPoints.push(
          new THREE.Vector3(
            800,
            -100,
            -800 + (1 / gridRes) * 1600 * 0.5 + i * (1 / gridRes) * 1600
          )
        );
      } else {
        fineGridPoints.push(
          new THREE.Vector3(
            800,
            -100,
            -800 + (1 / gridRes) * 1600 * 0.5 + i * (1 / gridRes) * 1600
          )
        );
        fineGridPoints.push(
          new THREE.Vector3(
            -800,
            -100,
            -800 + (1 / gridRes) * 1600 * 0.5 + i * (1 / gridRes) * 1600
          )
        );
      }
    }

    //Construct the lines with the geometry and the black material and add it to the scene (the whole grid consists of one cleverly placed line strip)
    const fineGridGeometry = new THREE.BufferGeometry().setFromPoints(
      fineGridPoints
    );
    this.fineGrid = new THREE.Line(fineGridGeometry, fineGridMaterial);
    this.fineGrid.visible = true;
    this.scene!.add(this.fineGrid);

    const coarseGridGeometry = new THREE.BufferGeometry().setFromPoints(
      coarseGridPoints
    );
    this.coarseGrid = new THREE.Line(coarseGridGeometry, coarseGridMaterial);
    this.coarseGrid.visible = true;
    this.scene!.add(this.coarseGrid);
  }

  loadGridHighlighter() {
    const lineWidth = 6;
    const boxMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
    const boxGeometryVertical = new THREE.BoxGeometry(
      lineWidth,
      lineWidth,
      1600
    );
    const boxGeometryHorizontal = new THREE.BoxGeometry(
      1600,
      lineWidth,
      lineWidth
    );

    const horizontalBox = new THREE.Mesh(boxGeometryVertical, boxMaterial);
    const verticalBox = new THREE.Mesh(boxGeometryHorizontal, boxMaterial);

    horizontalBox.position.set(-800, -100, 0);
    verticalBox.position.set(0, -100, -800);

    horizontalBox.visible = false;
    verticalBox.visible = false;

    this.scene!.add(horizontalBox);
    this.scene!.add(verticalBox);

    this.horizontalHighlighter = horizontalBox;
    this.verticalHighlighter = verticalBox;
  }

  loadZeroOutlineBoxes() {
    const outlineWidth = 4;
    const boxMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
    const boxGeometry125 = new THREE.BoxGeometry(
      outlineWidth,
      outlineWidth,
      1600
    );
    const boxGeometry346 = new THREE.BoxGeometry(
      1600,
      outlineWidth,
      outlineWidth
    );

    const box1 = new THREE.Mesh(boxGeometry125, boxMaterial);
    const box2 = new THREE.Mesh(boxGeometry125, boxMaterial);
    const box3 = new THREE.Mesh(boxGeometry346, boxMaterial);
    const box4 = new THREE.Mesh(boxGeometry346, boxMaterial);
    const box5 = new THREE.Mesh(boxGeometry125, boxMaterial);
    const box6 = new THREE.Mesh(boxGeometry346, boxMaterial);

    box1.position.set(-800, -100, 0);
    box2.position.set(800, -100, 0);
    box3.position.set(0, -100, -800);
    box4.position.set(0, -100, 800);

    box5.position.set(0, -100, 0);
    box6.position.set(0, -100, 0);

    this.scene!.add(box1);
    this.scene!.add(box2);
    this.scene!.add(box3);
    this.scene!.add(box4);
    this.scene!.add(box5);
    this.scene!.add(box6);

    this.outlineBoxes?.push(box1);
    this.outlineBoxes?.push(box2);
    this.outlineBoxes?.push(box3);
    this.outlineBoxes?.push(box4);
    this.zeroBoxes?.push(box5);
    this.zeroBoxes?.push(box6);
  }

  loadAxisDescription() {
    const loader = new FontLoader();
    loader.load("../helvetiker_regular.typeface.json", (font) => {
      const matDark = new THREE.LineBasicMaterial({
        color: 0x000000,
        side: THREE.DoubleSide,
      });

      for (let i = -8; i <= 8; i++) {
        const shapes = font.generateShapes(String(i), 100);

        const geometry = new THREE.ShapeGeometry(shapes);
        geometry.computeBoundingBox();
        const xMid =
          -0.5 * (geometry.boundingBox!.max.x - geometry.boundingBox!.min.x);
        geometry.translate(xMid, 0, 0);

        const scaling = 0.4;

        const horizontalDescriptorLeft = new THREE.Mesh(geometry, matDark);
        horizontalDescriptorLeft.scale.set(scaling, scaling, scaling);
        horizontalDescriptorLeft.rotation.set(-Math.PI / 2, 0, 0);
        horizontalDescriptorLeft.position.set(-840, -100, 10 - i * 100);
        this.scene!.add(horizontalDescriptorLeft);

        const verticalDescriptorDown = new THREE.Mesh(geometry, matDark);
        verticalDescriptorDown.scale.set(scaling, scaling, scaling);
        verticalDescriptorDown.rotation.set(-Math.PI / 2, 0, 0);
        verticalDescriptorDown.position.set(i * 100, -100, 850);
        this.scene!.add(verticalDescriptorDown);

        //Mirrored
        const horizontalDescriptorRight = new THREE.Mesh(geometry, matDark);
        horizontalDescriptorRight.scale.set(scaling, scaling, scaling);
        horizontalDescriptorRight.rotation.set(-Math.PI / 2, 0, 0);
        horizontalDescriptorRight.position.set(840, -100, 10 - i * 100);
        this.scene!.add(horizontalDescriptorRight);

        const verticalDescriptorUp = new THREE.Mesh(geometry, matDark);
        verticalDescriptorUp.scale.set(scaling, scaling, scaling);
        verticalDescriptorUp.rotation.set(-Math.PI / 2, 0, 0);
        verticalDescriptorUp.position.set(i * 100, -100, -850);
        this.scene!.add(verticalDescriptorUp);
      }
    });
  }

  /**
   * Saves that the mouse button is currently clicked down (done to prevent deselecting models
   * when the camera is moving).
   */
  onMouseDown(event: { clientX: number; clientY: number }) {
    this.mouseDown = true;

    if (this.container) {
      var rect = this.container.getBoundingClientRect();

      this.mouseStartPos.x =
        ((event.clientX - rect.left) / this.renderer!.domElement.clientWidth) *
          2 -
        1;
      this.mouseStartPos.y =
        -((event.clientY - rect.top) / this.renderer!.domElement.clientHeight) *
          2 +
        1;
    }
  }

  /**
   * Saves the position of the point in pointer.xy in NDC (so between [-1,1]). The pointer
   * coordinates will be used later on to perform the raycast.
   */
  onPointerMove(event: { clientX: number; clientY: number }) {
    if (this.pointer && this.container) {
      const ndcDist = Math.sqrt(
        Math.pow(this.pointer.x - this.mouseStartPos.x, 2) +
          Math.pow(this.pointer.y - this.mouseStartPos.y, 2)
      );
      const threshold = 0.01;

      if (this.mouseDown && ndcDist > threshold) {
        this.cameraMovement = true;
      }

      var rect = this.container.getBoundingClientRect();

      this.pointer.x =
        ((event.clientX - rect.left) / this.renderer!.domElement.clientWidth) *
          2 -
        1;
      this.pointer.y =
        -((event.clientY - rect.top) / this.renderer!.domElement.clientHeight) *
          2 +
        1;
    }
  }

  /**
   * Performs a raycast into the scene and writes the model uuid of the first hit object
   * into the variable selectedUuid.
   */
  onMouseUp(event: { clientX: number; clientY: number }) {
    this.mouseDown = false;
    if (this.cameraMovement) {
      this.cameraMovement = false;
      return;
    }

    if (event && this.pointer) {
      //Pointer must contain valid NDC coords in [-1, 1]
      if (
        this.pointer.x >= -1 &&
        this.pointer.x <= 1 &&
        this.pointer.y >= -1 &&
        this.pointer.y <= 1
      ) {
        this.raycaster!.setFromCamera(this.pointer!, this.camera!);

        // calculate objects intersecting the picking ray
        const intersects = this.raycaster!.intersectObjects(
          this.scene!.children,
          true
        );

        if (intersects.length > 0) {
          var selectedObject = intersects[0].object;
          //Dancer model was selected
          if (selectedObject.type == "SkinnedMesh") {
            this.selectedUuid = selectedObject.uuid;
            //Plane object was selected (no dancer will be selected)
          } else if (selectedObject.type == "Mesh") {
            this.selectedUuid = "plane";
          }
        }
      }
    }
  }

  onWindowResize() {
    const canvas = this.renderer!.domElement;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (canvas.width !== width || canvas.height !== height) {
      this.renderer!.setSize(width, height, false);
      this.camera!.aspect = width / height;
      this.camera!.updateProjectionMatrix();
    }
  }
}
